/**
 * Password checkup — the shared, client-side detection logic behind the
 * Security Checkup surface (weak · reused · exposed). One implementation for
 * every client (web, extension, Android, iOS), parity-tested like the card
 * helpers.
 *
 * Everything here is **pure and dependency-free**. The only check that needs
 * outside data — "exposed" (HIBP Pwned Passwords) — is split into its pure
 * halves (`pwnedPrefix` / `isPwned`); the SHA-1 digest and the network request
 * are per-client (WebCrypto on web/extension, native on Android/iOS) so this
 * module stays synchronous and testable. Nothing here ever emits a plaintext
 * password or a full hash.
 *
 * Privacy: weak + reused are computed entirely on-device over the already-
 * decrypted vault — no network, nothing leaves the device. See the design spec
 * `docs/superpowers/specs/2026-10-14-password-checkup-design.md` §6.
 */

/** Hard minimum length for a new safe (master) password. Shared by the
 *  registration form, the forgot-password reset, and the change-password modal
 *  so every entry point applies the same rule. (Moved here from web so there is
 *  a single source of truth across clients.) */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Returns a 0–1 strength score. Six equally-weighted signals: length ≥ 12,
 * length ≥ 16, and the presence of upper-case, lower-case, digit, and symbol.
 * This is the same score the `HbsStrengthMeter` renders on the signup / change
 * screens — kept identical so "strength" means one thing everywhere.
 */
export function passwordStrength(password: string): number {
  let score = 0;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score / 6;
}

/**
 * The strength below which a saved password counts as **weak** in the checkup.
 * 0.5 = fewer than three of the six strength signals — i.e. short and/or
 * single-character-class passwords. A single documented threshold so "weak" is
 * the same verdict in the meter and the checkup list.
 */
export const WEAK_STRENGTH_THRESHOLD = 0.5;

/** True when `password` falls below the weak threshold. Empty/undefined is not
 *  "weak" — it's simply not a password to check (the checkup filters those
 *  out upstream). */
export function isWeakPassword(password: string | null | undefined): boolean {
  if (!password) return false;
  return passwordStrength(password) < WEAK_STRENGTH_THRESHOLD;
}

/**
 * Groups of item ids that share an identical password — the "reused" check.
 * Compares on-device only and never emits the plaintext: the return value is
 * ids, not passwords. Items with an empty/absent password are ignored. A
 * password used by only one item is not a group.
 *
 * Deterministic: groups are ordered by their first member's id, and the ids
 * within each group preserve input order, so the output is stable for tests and
 * for a stable UI ordering.
 */
export function reusedGroups(
  items: ReadonlyArray<{ id: string; password?: string | null }>,
): string[][] {
  const byPassword = new Map<string, string[]>();
  for (const item of items) {
    if (!item.password) continue;
    const group = byPassword.get(item.password);
    if (group) group.push(item.id);
    else byPassword.set(item.password, [item.id]);
  }
  return [...byPassword.values()]
    .filter((ids) => ids.length >= 2)
    .sort((a, b) => a[0].localeCompare(b[0]));
}

/**
 * Split a SHA-1 hex digest into HIBP's k-anonymity range key: a 5-char prefix
 * (sent to the range API) and the 35-char suffix (matched locally against the
 * response). The caller computes the SHA-1 per-client and passes the 40-char
 * hex string in.
 *
 * NB: SHA-1 here is **HIBP's index format, not a security primitive** — don't
 * mistake it for weak crypto. The password's full hash never leaves the device;
 * only the 5-char prefix (shared by hundreds of unrelated hashes) is sent.
 */
export function pwnedPrefix(sha1Hex: string): { prefix: string; suffix: string } {
  const hex = sha1Hex.trim().toUpperCase();
  if (!/^[0-9A-F]{40}$/.test(hex)) {
    throw new Error('pwnedPrefix expects a 40-char SHA-1 hex digest');
  }
  return { prefix: hex.slice(0, 5), suffix: hex.slice(5) };
}

/**
 * How many k-anonymity prefixes a client sends per `POST /pwned-range` batch.
 * HIBP's range API is one prefix per request; our proxy accepts a batch and fans
 * out server-side, so a first checkup of a large safe costs ~1/25th the API
 * requests (and WAF budget). The server's own cap is `PWNED_RANGE_MAX_BATCH`
 * (CDK context `pwnedRangeMaxBatch`, default 25) — raise the server cap first,
 * then this. Android mirrors it as `PwnedRangeFetcher.BATCH_SIZE`.
 */
export const PWNED_RANGE_BATCH_SIZE = 25;

/** `POST /pwned-range` request body: 5-hex-char prefixes (deduped server-side). */
export interface PwnedRangeBatchRequest {
  prefixes: string[];
}

/**
 * `POST /pwned-range` 200 body: upper-cased prefix → padded range text, or `null`
 * when that one prefix's upstream fetch failed (fail-soft per prefix — the
 * client treats it as "couldn't check", never as clean).
 */
export interface PwnedRangeBatchResponse {
  ranges: Record<string, string | null>;
}

/**
 * Scan a Pwned Passwords range response for `suffix` and return its breach
 * **count** (prevalence), or 0 if absent. The response is lines of
 * `SUFFIX:count` (the suffix being the 35 chars after the 5-char prefix).
 *
 * Robust to the things the real API does: CRLF line endings, lower/upper-case
 * hex, and the `Add-Padding: true` rows — which are padding entries with a
 * **count of 0** and so, correctly, never register as a hit. A matched suffix
 * whose count parses to 0 (padding) returns 0 = "not exposed".
 */
export function isPwned(rangeText: string, suffix: string): number {
  const want = suffix.trim().toUpperCase();
  for (const rawLine of rangeText.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const sep = line.indexOf(':');
    if (sep === -1) continue;
    const lineSuffix = line.slice(0, sep).toUpperCase();
    if (lineSuffix !== want) continue;
    const count = Number.parseInt(line.slice(sep + 1), 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }
  return 0;
}
